/**
 * useTTS — Native Japanese audio playback via ElevenLabs (Yoko / Kenzo).
 *
 * Design decisions (DO NOT regress):
 *  - NO browser SpeechSynthesis fallback. The browser voice is non-native and
 *    sometimes reads kanji in Chinese. For a Japanese learning product this is
 *    unacceptable. If ElevenLabs fails we surface a clear error toast.
 *  - Voice preference comes from VoiceContext (localStorage). Callers may
 *    override per call (e.g. dialogue speakers A/B in Lessons) by passing voice.
 *  - In-memory blob cache keyed by (voice, text). Backend also caches forever
 *    in MongoDB, so even a cold reload is fast after the first generation.
 *  - 15s timeout + 1 retry on network errors (helps iPhone Safari on flaky 4G).
 */
import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { useVoice } from "../context/VoiceContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const memCache = new Map(); // key = `${voice}|${text}` → blob URL
const inflight = new Map(); // key → Promise (dedupe concurrent requests)

// Cache-bust token. Bump this whenever the ElevenLabs model changes so that
// any MP3s the browser cached from a previous model (e.g. eleven_multilingual_v2
// with residual Mandarin accent) are NEVER served again. The query param
// doesn't affect backend cache lookups (text+voice are the only keys used
// server-side).
//   turbo25c = eleven_turbo_v2_5 + language_code=ja + short-kana repeat expansion
const TTS_VERSION = "turbo25c";

const REQUEST_TIMEOUT_MS = 15000;

async function fetchWithTimeout(url, timeout) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const r = await fetch(url, { signal: controller.signal });
    return r;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchAudio(text, voice) {
  const key = `${voice}|${text}`;
  if (memCache.has(key)) return memCache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const url = `${BACKEND}/api/tts?text=${encodeURIComponent(text)}&voice=${voice}&v=${TTS_VERSION}`;
  const exec = async () => {
    let lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetchWithTimeout(url, REQUEST_TIMEOUT_MS);
        if (!r.ok) {
          // 429 = rate limit → don't retry, surface immediately
          if (r.status === 429) throw new Error("RATE_LIMIT");
          throw new Error(`HTTP_${r.status}`);
        }
        const blob = await r.blob();
        if (!blob || blob.size < 200) throw new Error("EMPTY_AUDIO");
        const blobUrl = URL.createObjectURL(blob);
        memCache.set(key, blobUrl);
        return blobUrl;
      } catch (e) {
        lastErr = e;
        if (String(e.message) === "RATE_LIMIT") throw e;
        // small backoff before retry
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    throw lastErr;
  };

  const promise = exec().finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
}

export function useTTS() {
  const audioRef = useRef(null);
  const loadingRef = useRef(false);
  const { voice: prefVoice } = useVoice();

  const speak = useCallback(async (text, voiceOverride) => {
    if (!text) return;
    if (loadingRef.current) return; // debounce double-clicks
    loadingRef.current = true;
    const voice = voiceOverride || prefVoice || "female";
    try {
      // Stop anything currently playing
      if (audioRef.current) {
        try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_e) { /* noop */ }
      }
      const blobUrl = await fetchAudio(text, voice);
      const a = new Audio(blobUrl);
      audioRef.current = a;
      await a.play();
    } catch (e) {
      const msg = String(e?.message || e);
      if (msg === "RATE_LIMIT") {
        toast.error("Trop de lectures audio. Réessaie dans une minute 🙏");
      } else if (msg.startsWith("HTTP_4") || msg.startsWith("HTTP_5") || msg === "EMPTY_AUDIO") {
        toast.error("Audio momentanément indisponible. Réessaie dans un instant.");
      } else {
        // Network / abort
        toast.error("Connexion lente — audio en attente. Réessaie 🙏");
      }
      // eslint-disable-next-line no-console
      console.warn("[useTTS] playback failed:", msg);
    } finally {
      loadingRef.current = false;
    }
  }, [prefVoice]);

  /**
   * Warm up the backend cache for a list of words.
   * Fire-and-forget; failures are silent. Useful to pre-generate audio
   * when a page mounts so the first user click is instant.
   */
  const prefetch = useCallback((texts, voiceOverride) => {
    const voice = voiceOverride || prefVoice || "female";
    if (!Array.isArray(texts)) return;
    texts.slice(0, 40).forEach((t) => {
      if (!t) return;
      const key = `${voice}|${t}`;
      if (memCache.has(key) || inflight.has(key)) return;
      // fire-and-forget: errors swallowed (the user-initiated click will surface them)
      fetchAudio(t, voice).catch(() => {});
    });
  }, [prefVoice]);

  return { speak, prefetch };
}

export default useTTS;
